import { useState } from "react";
import type { Dispatch, SetStateAction } from "react";
import type { ApiClient, Angebot, Unternehmen } from "@kundeninfo/api-client";
import { angebotKopffelder, leererAngebotEntwurf, pruefeAngebot,
         zeigeAngebotsFehlerFeld, zeigeAngebotsFeld } from "./AngebotDetail.js";
import type { AngebotEntwurf } from "./AngebotDetail.js";
import { useKopffaltung } from "./Kopffaltung.js";
import type { Kopffaltung } from "./Kopffaltung.js";
import { apiMeldung } from "./vorgangsdetail.js";

/**
 * Das ANLEGEN eines Angebots als wiederverwendbarer Hook (Bereichs-Spalten, Etappe 1,
 * Aufgabe 2, Fixrunde 1).
 *
 * Nach dem ersten Bausteinschnitt lag der Anlege-Weg noch im Akten-Reiter — und darin zwei
 * Feinheiten, die man beim Nachbauen im Bereich (Aufgabe 3) mit hoher Wahrscheinlichkeit
 * verlöre:
 *
 *  1. **Anlegen UND Vorlage anwenden in EINEM Zug.** `vorlageId` geht mit `createOffer`
 *     hinaus, der Server schreibt Kopf, Positionen, Texte, Periodenregel und Zahlplan in
 *     EINER Transaktion (seit Migration 029 — vorher waren es zwei Aufrufe mit einer
 *     Vorprüfung davor). Ein Fehlschlag lässt deshalb nichts zurück: kein leeres Angebot,
 *     keine verbrauchte Nummer.
 *  2. **Mit Vorlage geht der neue Vorgang GLEICH ins Bearbeiten.** Genau das ist Dirks
 *     Muss-Feature — wählen, dann nur noch die Mengen füllen. Ohne Vorlage bleibt die Liste
 *     stehen: Ein aufgeklappter, leerer Editor verdeckte dort nur die Statusknöpfe des
 *     neuen Angebots, ohne etwas zu zeigen.
 *
 * Der Hook führt den Entwurf, die Feldmeldung und den Speichert-Riegel; WOHIN das Ergebnis
 * fällt, entscheidet der Aufrufer über drei Rückrufe. Er weiss nichts von Akte oder
 * Bereich — die Kundenauswahl, die der Bereich davorsetzt, findet ausserhalb statt und
 * mündet einzig in `companies` und `kundeId`.
 */

export type AngebotAnlegenClient = Pick<ApiClient, "createOffer">;

export type AngebotAnlegen = {
  entwurf: AngebotEntwurf;
  setEntwurf: Dispatch<SetStateAction<AngebotEntwurf>>;
  feldFehler: string | null;
  setFeldFehler: Dispatch<SetStateAction<string | null>>;
  /** Läuft gerade ein `createOffer`? Sperrt Anlegen und Abbrechen. */
  speichert: boolean;
  /**
   * Der Stand, mit dem die Maske aufgemacht hat — die Vergleichsgrundlage für die Rückfrage
   * vor dem Abbrechen (Hausregel „striktes Modalpanel", 06.09.2026).
   *
   * Er gehört dem Hook und nicht dem Aufrufer: Nur hier steht, WIE ein frischer Entwurf
   * aussieht, und ein nachgebauter Vergleichsstand beim Aufrufer liefe beim nächsten neuen
   * Feld auseinander — die Rückfrage überginge es stillschweigend.
   */
  ausgang: AngebotEntwurf;
  /**
   * Die Einfaltung des Kopfblocks — beim ANLEGEN aufgeklappt (Entscheid Dirk 08.09.2026):
   * Am leeren Vorgang gibt es nichts zu übergehen, Titel und Betrag müssen ohnehin
   * eingetragen werden, und ein zugeklappter Pflichtblock wäre der erste Klick jeder
   * Anlage.
   *
   * Sie gehört dem HOOK und nicht dem Aufrufer, aus demselben Grund wie `ausgang`: Hier
   * steht, wann eine Anlage von vorn beginnt (`zuruecksetzen`), und hier läuft die Prüfung,
   * die einen Befund auf ein eingeklapptes Feld werfen kann.
   */
  kopf: Kopffaltung;
  /** Entwurf, Feldmeldung und Einfaltung auf Anfang — beim Öffnen, Schliessen und beim
   *  Wechsel ins Bearbeiten eines bestehenden Vorgangs. */
  zuruecksetzen: () => void;
  anlegen: () => Promise<void>;
};

export function useAngebotAnlegen(arg: {
  client: AngebotAnlegenClient;
  kundeId: string;
  /** Die wählbaren Unternehmen — bei genau einem ist es vorgewählt. */
  companies: Unternehmen[];
  /** Das Arbeitsunternehmen aus der Kopfleiste (ME-Etappe 1, Aufgabe 7): vorgewählt, wenn
   *  es unter den wählbaren steht. `null`: Bestand. */
  arbeitsunternehmenId: string | null;
  /**
   * Das Präfix der Feld-Kennungen der Maske, die diesen Hook benutzt („kang-neu" in der
   * Akte, „bang-neu" im Bereich).
   *
   * Er braucht es, um bei einem Feldbefund GENAU DAS Feld zu zeigen (`kopf.zeigeFeld`).
   * Die Prüfung nennt nur die Endung der Kennung — welches Formular gerade läuft, weiss
   * allein sein Aufrufer.
   */
  feldPraefix: string;
  /** Nach dem Anlegen: Der Aufrufer schliesst seine Anlage. */
  onSchliessen: () => void;
  /** Nach dem Anlegen: neu laden und die FRISCHE Liste zurückgeben — aus ihr stammt der
   *  Satz, der bei Vorlagen gleich aufgeschlagen wird (er trägt die aufgelösten Namen). */
  onNeuLaden: () => Promise<Angebot[]>;
  /** Mit Vorlage angelegt: diesen Vorgang gleich ins Bearbeiten holen. */
  onBearbeiten: (angebot: Angebot, meldung: string) => void;
}): AngebotAnlegen {
  const [entwurf, setEntwurf] = useState<AngebotEntwurf>(() =>
    leererAngebotEntwurf(arg.companies, arg.arbeitsunternehmenId));
  const [ausgang, setAusgang] = useState<AngebotEntwurf>(() =>
    leererAngebotEntwurf(arg.companies, arg.arbeitsunternehmenId));
  const [feldFehler, setFeldFehler] = useState<string | null>(null);
  const [speichert, setSpeichert] = useState(false);
  const kopf = useKopffaltung(true);

  function zuruecksetzen(): void {
    const frisch = leererAngebotEntwurf(arg.companies, arg.arbeitsunternehmenId);
    setEntwurf(frisch);
    setAusgang(frisch);
    setFeldFehler(null);
    kopf.zuruecksetzen();
  }

  async function anlegen(): Promise<void> {
    const problem = pruefeAngebot(entwurf, true);
    if (problem !== null) {
      setFeldFehler(problem.meldung);
      // Aufklappen und hineinsehen: Der Block ist beim Anlegen zwar offen, aber der
      // Bearbeiter kann ihn zugeklappt haben — dann stünde die Meldung über einem Feld,
      // das nicht zu sehen ist.
      zeigeAngebotsFeld(kopf, arg.feldPraefix, problem.feld);
      return;
    }
    setSpeichert(true);
    setFeldFehler(null);
    const vorlageId = entwurf.vorlageId === "" ? null : entwurf.vorlageId;
    try {
      const angelegt = await arg.client.createOffer(
        arg.kundeId,
        { companyId: entwurf.companyId, vorlageId, ...angebotKopffelder(entwurf) });
      arg.onSchliessen();
      const frisch = await arg.onNeuLaden();
      // Bevorzugt der Satz aus der frisch geladenen Liste; fehlt er dort, dient die Antwort
      // selbst (siehe Kopf, Punkt 2).
      if (vorlageId !== null) {
        const offen = frisch.find((a) => a.id === angelegt.id) ?? angelegt;
        arg.onBearbeiten(offen,
          "Die Vorlage ist angewandt — jetzt nur noch die Mengen füllen.");
      }
    } catch (e) {
      setFeldFehler(apiMeldung(e, "Das Angebot konnte nicht angelegt werden."));
      // Die zweite Quelle von Feldbefunden: Der Server prüft mehr als die Maske
      // (Titel-Höchstlänge, Betragsgrenzen, halbe Kündigungsfrist, fehlende
      // Kundenbeziehung). Nennt er ein Feld im Kopfblock, wird es sichtbar gemacht.
      zeigeAngebotsFehlerFeld(kopf, arg.feldPraefix, e);
    } finally {
      setSpeichert(false);
    }
  }

  return { entwurf, setEntwurf, feldFehler, setFeldFehler, speichert, ausgang, kopf,
           zuruecksetzen, anlegen };
}
